import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { getFirstCalendar, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { map, take } from 'rxjs/operators';
import { BOOM_PUMP, BOOM_PUMP_CODE } from '../../../rms-specification/constants/pumps';
import { UnloadTypePumpCodes } from '@ordersnproducts/modules/shared/models/pump-type.enum';

@Injectable()
export class HandlePumpCapacityCommand implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  async onFieldChanged(value: string, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): Promise<void> {
    // Reset pump length
    formGroup.get(SpecFieldNames.PUMP_LENGTH).setValue(undefined);
    if (this.isBoomPump(formGroup)) {
      const isSpecificPumpSize = await this.isSpecificPumpSize(formGroup);
      this.disableAndHide(SpecFieldNames.PUMP_LENGTH, form, !isSpecificPumpSize);
    }
  }

  private isBoomPump(formGroup: UntypedFormGroup) {
    return formGroup.get(SpecFieldNames.UNLOAD_TYPE).value === BOOM_PUMP;
  }

  private isSpecificPumpSize(formGroup: UntypedFormGroup) {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((calendar) => {
          const selectedId = formGroup.get(SpecFieldNames.PUMP).value;
          const placingBoomPump =
            calendar && calendar.pump.unloadMethods.find((method) => method.unloadMethodCode === BOOM_PUMP_CODE);
          const selectedPumpSize =
            placingBoomPump &&
            placingBoomPump.listPumpPen.find((item) => {
              return item.pumpSizeCode === selectedId;
            });
          return selectedPumpSize && !!selectedPumpSize.flagSpecificSize;
        }),
      )
      .toPromise();
  }

  private disableAndHide(field: SpecFieldNames, form: ConfigurableFormComponent, shouldHide: boolean) {
    form.setDisabled(field, shouldHide);
    form.setHidden(field, shouldHide);
  }
}

/**
 * For UL, this class is used to handle the pump length field when the pump is a specific pump.
 * If the pump is a specific pump, the pump length field should be enabled and shown.
 * Otherwise, the pump length field should be disabled and hidden.
 * @export
 * @class HandlePumpLengthCommandUL
 * @implements {OnFieldChanged}
 */
@Injectable()
export class HandlePumpLengthCommandUL implements OnFieldChanged {
  constructor() {}
  async onFieldChanged(value: string, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): Promise<void> {
    // Reset pump length
    formGroup.get(SpecFieldNames.PUMP_LENGTH).setValue(undefined);
    form.updateValues(SpecFieldNames.PUMP_LENGTH);

    this.isSpecificPump(formGroup)
      ? this.disableAndHide(SpecFieldNames.PUMP_LENGTH, form, false)
      : this.disableAndHide(SpecFieldNames.PUMP_LENGTH, form, true);
  }

  private isSpecificPump(formGroup: UntypedFormGroup) {
    return formGroup.get(SpecFieldNames.PUMP).value === UnloadTypePumpCodes.SPECIFIC_PUMP;
  }

  private disableAndHide(field: SpecFieldNames, form: ConfigurableFormComponent, shouldHide: boolean) {
    form.setDisabled(field, shouldHide);
    form.setHidden(field, shouldHide);
  }
}
